import { supabase } from './supabase';

export async function globalSearch(query) {
  if (!query || query.trim().length < 2) return { profiles: [], jobs: [], castings: [] };
  const q = query.trim().toLowerCase();
  const [profilesRes, jobsRes, castingsRes] = await Promise.all([
    supabase.from('profiles').select('id, handle, display_name, avatar_url, crafts, location').or(`display_name.ilike.%${q}%,handle.ilike.%${q}%`).limit(8),
    supabase.from('jobs').select('id, title, type, rate, location, poster_id').ilike('title', `%${q}%`).eq('status', 'active').limit(8),
    supabase.from('castings').select('id, title, type, location, rate').ilike('title', `%${q}%`).limit(8),
  ]);
  return {
    profiles: profilesRes.data || [],
    jobs: jobsRes.data || [],
    castings: castingsRes.data || [],
  };
}

export async function getAllProfiles() {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) console.error('getAllProfiles:', error);
  return data || [];
}

export async function getProfile(userId) {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
  if (error) console.error('getProfile:', error);
  return data;
}

export async function updateProfile(userId, updates) {
  const { data, error } = await supabase.from('profiles').update(updates).eq('id', userId);
  if (error) console.error('updateProfile:', error);
  return data;
}

export async function getPosts() {
  const { data, error } = await supabase.from('posts').select('*, profiles(id, handle, display_name, avatar_url, crafts), post_likes(count), post_comments(count)').order('created_at', { ascending: false }).limit(50);
  if (error) console.error('getPosts:', error);
  return data || [];
}

export async function createPost(userId, content, mediaUrls = [], emberHours = null) {
  const emberExpiry = emberHours ? new Date(Date.now() + emberHours * 60 * 60 * 1000).toISOString() : null;
  const { data, error } = await supabase.from('posts').insert({ user_id: userId, content, media_urls: mediaUrls, ember_expires_at: emberExpiry }).select().single();
  if (error) console.error('createPost:', error);
  return data;
}

export async function deletePost(postId) {
  const { error } = await supabase.from('posts').delete().eq('id', postId);
  if (error) console.error('deletePost:', error);
}

export async function getJobs() {
  const { data, error } = await supabase.from('jobs').select('*, profiles(id, handle, display_name)').eq('status', 'active').order('created_at', { ascending: false });
  if (error) console.error('getJobs:', error);
  return data || [];
}

export async function createJob(posterId, jobData) {
  const { data, error } = await supabase.from('jobs').insert({ poster_id: posterId, ...jobData }).select().single();
  if (error) console.error('createJob:', error);
  return data;
}

export async function getConnections(userId) {
  const { data, error } = await supabase.from('connections').select('*, requester:profiles!connections_requester_id_fkey(id, handle, display_name, avatar_url, crafts), addressee:profiles!connections_addressee_id_fkey(id, handle, display_name, avatar_url, crafts)').or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
  if (error) console.error('getConnections:', error);
  return data || [];
}

export async function sendConnectionRequest(requesterId, addresseeId) {
  const { data, error } = await supabase.from('connections').insert({ requester_id: requesterId, addressee_id: addresseeId, status: 'pending' }).select().single();
  if (error) console.error('sendConnectionRequest:', error);
  return data;
}

export async function updateConnectionStatus(connectionId, status) {
  const { data, error } = await supabase.from('connections').update({ status }).eq('id', connectionId).select().single();
  if (error) console.error('updateConnectionStatus:', error);
  return data;
}

export async function getMessages(userId, otherUserId) {
  const { data, error } = await supabase.from('messages').select('*').or(`and(sender_id.eq.${userId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${userId})`).order('created_at', { ascending: true });
  if (error) console.error('getMessages:', error);
  return data || [];
}

export async function sendMessage(senderId, receiverId, content) {
  const { data, error } = await supabase.from('messages').insert({ sender_id: senderId, receiver_id: receiverId, content }).select().single();
  if (error) console.error('sendMessage:', error);
  return data;
}

export async function getVerificationSignals(userId) {
  const { data, error } = await supabase.from('verification_signals').select('*').eq('user_id', userId);
  if (error && error.code !== 'PGRST116') console.error('getVerificationSignals:', error);
  return data;
}

export async function upsertVerificationSignals(userId, signals) {
  const { data, error } = await supabase.from('verification_signals').upsert({ user_id: userId, ...signals, updated_at: new Date().toISOString() }).select();
  if (error) console.error('upsertVerificationSignals:', error);
  return data;
}

// ── INVITE CODES ──
export async function createInviteCode(createdBy, note = '') {
  const code = 'ARTIZAN-' + Math.random().toString(36).substring(2, 8).toUpperCase();
  const { data, error } = await supabase.from('invite_codes').insert({ code, created_by: createdBy, note }).select().single();
  if (error) { console.error('createInviteCode:', error); return null; }
  return data;
}

export async function getInviteCodes(createdBy) {
  const { data, error } = await supabase.from('invite_codes').select('*').eq('created_by', createdBy).order('created_at', { ascending: false });
  if (error) { console.error('getInviteCodes:', error); return []; }
  return data || [];
}

export async function checkInviteCode(code) {
  const { data, error } = await supabase
    .from('invite_codes')
    .select('*')
    .eq('code', code.toUpperCase().trim())
    .is('used_by', null)
    .single();
  if (error) return null;
  return data;
}

export async function markCodeUsed(code, userId) {
  const { error } = await supabase
    .from('invite_codes')
    .update({ used_by: userId, used_at: new Date().toISOString() })
    .eq('code', code.toUpperCase().trim());
  if (error) console.error('markCodeUsed:', error);
}

export async function generateInviteCode(createdBy, note = '') {
  const code = Math.random().toString(36).substring(2, 8).toUpperCase();
  const { data, error } = await supabase
    .from('invite_codes')
    .insert({ code, created_by: createdBy, note })
    .select()
    .single();
  if (error) console.error('generateInviteCode:', error);
  return data;
}

// ── STORAGE / FILE UPLOADS ──
export async function uploadMedia(userId, file, folder = 'posts') {
  const ext = file.name.split('.').pop();
  const filename = `${userId}/${folder}/${Date.now()}.${ext}`;
  
  const { error } = await supabase.storage
    .from('media')
    .upload(filename, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    });
  
  if (error) { console.error('uploadMedia:', error); return null; }
  
  const { data: urlData } = supabase.storage
    .from('media')
    .getPublicUrl(filename);
  
  return urlData.publicUrl;
}

export async function uploadAvatar(userId, file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      await updateProfile(userId, { avatar_url: base64 });
      resolve(base64);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}
export async function getSubscriptionTier(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('subscription_tier')
    .eq('id', userId)
    .single();
  if (error) console.error('getSubscriptionTier:', error);
  return data?.subscription_tier || 'free';
}

export async function updateSubscriptionTier(userId, tier) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ subscription_tier: tier })
    .eq('id', userId);
  if (error) console.error('updateSubscriptionTier:', error);
  return data;
}

export async function getThreads(userId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*, sender:profiles!messages_sender_id_fkey(id, display_name, avatar_url), receiver:profiles!messages_receiver_id_fkey(id, display_name, avatar_url)')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
    .order('created_at', { ascending: false });
  if (error) { console.error('getThreads:', error); return []; }
  
  // Group by the other participant
  const threads = {};
  (data || []).forEach(msg => {
    const otherId = msg.sender_id === userId ? msg.receiver_id : msg.sender_id;
    const otherProfile = msg.sender_id === userId ? msg.receiver : msg.sender;
    if (!threads[otherId]) {
      threads[otherId] = {
        threadId: otherId,
        participantId: otherId,
        participant: otherProfile?.display_name || 'Unknown',
        avatarUrl: otherProfile?.avatar_url || null,
        preview: msg.content,
        time: new Date(msg.created_at).toLocaleDateString(),
        unread: 0,
      };
    }
  });
  return Object.values(threads);
}

export async function getCastings() {
  const { data, error } = await supabase
    .from('castings')
    .select('*, poster:profiles!castings_posted_by_fkey(id, handle, display_name, avatar_url)')
    .eq('status', 'open')
    .order('created_at', { ascending: false });
  if (error) { console.error('getCastings:', error); return []; }
  return data || [];
}

export async function postCasting(userId, casting) {
  const { data, error } = await supabase
    .from('castings')
    .insert({ ...casting, posted_by: userId })
    .select()
    .single();
  if (error) { console.error('postCasting:', error); return null; }
  return data;
}

export async function applyCasting(castingId, applicantId, note) {
  const { data, error } = await supabase
    .from('casting_applications')
    .insert({ casting_id: castingId, applicant_id: applicantId, note })
    .select()
    .single();
  if (error) { console.error('applyCasting:', error); return null; }
  return data;
}

export async function getCastingApplications(castingId) {
  const { data, error } = await supabase
    .from('casting_applications')
    .select('*, applicant:profiles!casting_applications_applicant_id_fkey(id, handle, display_name, avatar_url, crafts)')
    .eq('casting_id', castingId)
    .order('created_at', { ascending: false });
  if (error) { console.error('getCastingApplications:', error); return []; }
  return data || [];
}

export async function shortlistApplicant(applicationId, shortlisted) {
  const { data, error } = await supabase
    .from('casting_applications')
    .update({ shortlisted })
    .eq('id', applicationId)
    .select()
    .single();
  if (error) { console.error('shortlistApplicant:', error); return null; }
  return data;
}

export async function getSuggestedConnections(userId) {
  const { data: connections } = await supabase.from('connections').select('requester_id, addressee_id').or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
  const connectedIds = new Set((connections || []).flatMap(c => [c.requester_id, c.addressee_id]));
  connectedIds.add(userId);
  const { data, error } = await supabase.from('profiles').select('id, handle, display_name, avatar_url, crafts').order('created_at', { ascending: false }).limit(20);
  if (error) { console.error('getSuggestedConnections:', error); return []; }
  return (data || []).filter(p => !connectedIds.has(p.id));
}

export async function getProfileStats(userId) {
  const [views, connections, applications, shortlists] = await Promise.all([
    supabase.from('profile_views').select('id', { count: 'exact', head: true }).eq('profile_id', userId),
    supabase.from('connections').select('id', { count: 'exact', head: true }).or(`requester_id.eq.${userId},addressee_id.eq.${userId}`),
    supabase.from('casting_applications').select('id', { count: 'exact', head: true }).eq('applicant_id', userId),
    supabase.from('casting_applications').select('id', { count: 'exact', head: true }).eq('applicant_id', userId).eq('shortlisted', true),
  ]);
  return {
    profileViews: views.count || 0,
    connectionRequests: connections.count || 0,
    castingApplications: applications.count || 0,
    castingShortlists: shortlists.count || 0,
  };
}

export async function getNotifications(userId) {
  const { data, error } = await supabase
    .from('notifications')
    .select('*, actor:profiles!notifications_actor_id_fkey(id, handle, display_name, avatar_url)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(30);
  if (error) { console.error('getNotifications:', error); return []; }
  return data || [];
}

export async function markNotificationsRead(userId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) console.error('markNotificationsRead:', error);
}

export async function createNotification(userId, type, title, message, actorId, referenceId) {
  const { error } = await supabase
    .from('notifications')
    .insert({ user_id: userId, type, title, message, actor_id: actorId, reference_id: referenceId });
  if (error) console.error('createNotification:', error);
}

export async function recordProfileView(profileId, viewerId) {
  if (profileId === viewerId) return;
  const { error } = await supabase
    .from('profile_views')
    .insert({ profile_id: profileId, viewer_id: viewerId });
  if (error) console.error('recordProfileView:', error);
}

export async function followUser(followerId, followingId) {
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: followerId, following_id: followingId });
  if (error) console.error('followUser:', error);
}

export async function unfollowUser(followerId, followingId) {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);
  if (error) console.error('unfollowUser:', error);
}

export async function getFollowStatus(followerId, followingId) {
  const { data } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .single();
  return !!data;
}

export async function getFollowCounts(userId) {
  const [followers, following] = await Promise.all([
    supabase.from('follows').select('id', { count: 'exact', head: true }).eq('following_id', userId),
    supabase.from('follows').select('id', { count: 'exact', head: true }).eq('follower_id', userId),
  ]);
  return { followers: followers.count || 0, following: following.count || 0 };
}

export async function likePost(postId, userId) {
  const { error } = await supabase
    .from('post_likes')
    .insert({ post_id: postId, user_id: userId });
  if (error) console.error('likePost:', error);
}

export async function unlikePost(postId, userId) {
  const { error } = await supabase
    .from('post_likes')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId);
  if (error) console.error('unlikePost:', error);
}

export async function getPostLikes(postId) {
  const { data, count } = await supabase
    .from('post_likes')
    .select('user_id', { count: 'exact' })
    .eq('post_id', postId);
  return { count: count || 0, users: (data || []).map(d => d.user_id) };
}

export async function addComment(postId, userId, content) {
  const { data, error } = await supabase
    .from('post_comments')
    .insert({ post_id: postId, user_id: userId, content })
    .select('*, profiles(id, handle, display_name, avatar_url)')
    .single();
  if (error) { console.error('addComment:', error); return null; }
  return data;
}

export async function getComments(postId) {
  const { data, error } = await supabase
    .from('post_comments')
    .select('*, profiles(id, handle, display_name, avatar_url)')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
  if (error) { console.error('getComments:', error); return []; }
  return data || [];
}

export async function getJobApplicants(jobId) {
  const { data, error } = await supabase
    .from('job_applications')
    .select('*, applicant:profiles!job_applications_applicant_id_fkey(id, handle, display_name, avatar_url, crafts)')
    .eq('job_id', jobId)
    .order('applied_at', { ascending: false });
  if (error) { console.error('getJobApplicants:', error); return []; }
  return data || [];
}

export async function applyToJob(jobId, applicantId, note) {
  const { data, error } = await supabase
    .from('job_applications')
    .insert({ job_id: jobId, applicant_id: applicantId, note })
    .select()
    .single();
  if (error) { console.error('applyToJob:', error); return null; }
  return data;
}

export async function checkJobApplication(jobId, applicantId) {
  const { data } = await supabase
    .from('job_applications')
    .select('id')
    .eq('job_id', jobId)
    .eq('applicant_id', applicantId)
    .single();
  return !!data;
}

// ── FEATURE TRACKING ──
export async function getFeatureProgress(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('features_explored, feature_reminder_last_shown')
    .eq('id', userId)
    .single();
  if (error) console.error('getFeatureProgress:', error);
  return data || { features_explored: {}, feature_reminder_last_shown: null };
}

export async function markFeatureExplored(userId, feature) {
  const { data: current } = await supabase
    .from('profiles')
    .select('features_explored')
    .eq('id', userId)
    .single();
  const existing = current?.features_explored || {};
  if (existing[feature]) return; // already marked
  const updated = { ...existing, [feature]: new Date().toISOString() };
  const { error } = await supabase
    .from('profiles')
    .update({ features_explored: updated })
    .eq('id', userId);
  if (error) console.error('markFeatureExplored:', error);
}

export async function markReminderShown(userId) {
  const { error } = await supabase
    .from('profiles')
    .update({ feature_reminder_last_shown: new Date().toISOString() })
    .eq('id', userId);
  if (error) console.error('markReminderShown:', error);
}

// ── VIZION BOARDS ──
export async function getVizionBoards(userId) {
  const { data, error } = await supabase.from('vizion_boards').select('*').eq('owner_id', userId).order('updated_at', { ascending: false });
  if (error) console.error('getVizionBoards:', error);
  return data || [];
}
export async function saveVizionBoard(userId, board, scenes) {
  const payload = { ...board, owner_id: userId, scenes: JSON.stringify(scenes), updated_at: new Date().toISOString() };
  const { data, error } = board.id
    ? await supabase.from('vizion_boards').update(payload).eq('id', board.id).select().single()
    : await supabase.from('vizion_boards').insert(payload).select().single();
  if (error) console.error('saveVizionBoard:', error);
  return data;
}
export async function deleteVizionBoard(id) {
  const { error } = await supabase.from('vizion_boards').delete().eq('id', id);
  if (error) console.error('deleteVizionBoard:', error);
}

// ── CURTAINZ PROJECTS ──
export async function getCurtainzProjects(userId) {
  const { data, error } = await supabase.from('curtainz_projects').select('*').eq('owner_id', userId).order('updated_at', { ascending: false });
  if (error) console.error('getCurtainzProjects:', error);
  return data || [];
}
export async function saveCurtainzProject(userId, project) {
  const payload = { ...project, owner_id: userId, updated_at: new Date().toISOString() };
  const { data, error } = project.id
    ? await supabase.from('curtainz_projects').update(payload).eq('id', project.id).select().single()
    : await supabase.from('curtainz_projects').insert(payload).select().single();
  if (error) console.error('saveCurtainzProject:', error);
  return data;
}
